import { useRef, type ElementType, type KeyboardEvent } from "react";

import { cn } from "../lib/cn";
import { INSET_GROUP_CLASS } from "../lib/inset";

export interface SegmentedOption<Value extends string> {
  value: Value;
  label: string;
  title?: string;
  icon?: ElementType;
  count?: number;
}

export interface SegmentedControlProps<Value extends string> {
  label: string;
  value: Value;
  options: readonly SegmentedOption<Value>[];
  onChange: (value: Value) => void;
  variant?: "default" | "inset" | "underline";
  className?: string;
}

export function SegmentedControl<Value extends string>({
  label,
  value,
  options,
  onChange,
  variant = "default",
  className,
}: SegmentedControlProps<Value>) {
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (options.length === 0) return;
    const direction =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? -1
          : 0;

    if (direction === 0 && event.key !== "Home" && event.key !== "End") return;
    event.preventDefault();
    const nextIndex =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? options.length - 1
          : (index + direction + options.length) % options.length;
    onChange(options[nextIndex].value);
    itemRefs.current[nextIndex]?.focus();
  }

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(
        variant === "underline"
          ? "flex min-w-0 gap-1 overflow-x-auto border-b border-border"
          : cn(
              "density-control-lg inline-grid grid-flow-col auto-cols-fr items-center rounded-control p-1",
              variant === "inset"
                ? cn("gap-0.5", INSET_GROUP_CLASS)
                : "border border-border bg-surface",
            ),
        className,
      )}
    >
      {options.map((option, index) => {
        const selected = value === option.value;
        const Icon = option.icon;

        return (
          <button
            key={option.value}
            ref={(element) => {
              itemRefs.current[index] = element;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={
              selected || (index === 0 && !options.some((item) => item.value === value)) ? 0 : -1
            }
            title={option.title}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={cn(
              "inline-flex shrink-0 items-center justify-center gap-1.5 font-medium transition-colors",
              variant === "underline"
                ? "-mb-px min-h-(--density-control-md) border-b-2 px-3 py-2 text-sm"
                : "density-control-sm rounded-md px-2.5 text-xs font-semibold",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus",
              variant === "underline" && "focus-visible:ring-inset",
              variant === "underline"
                ? selected
                  ? "border-focus text-primary"
                  : "border-transparent text-muted hover:text-primary"
                : selected
                  ? variant === "inset"
                    ? "bg-surface text-interaction-text"
                    : "bg-brand-cyan/10 text-interaction-text"
                  : "text-secondary hover:bg-surface-elevated hover:text-primary",
            )}
          >
            {Icon ? <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.7} aria-hidden /> : null}
            {option.label}
            {option.count != null ? (
              <span className="text-xs font-normal tabular-nums text-muted">{option.count}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
